import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Training, MonthlyTotal, GetTrainingsParams, UpdateTrainingStatusParams, UpdateTrainingNotesParams } from '@/types/trainings';
import { useCallback } from 'react';
import { checkTrainingGoalProgress } from '@/utils/trainingGoalNotifications';
import { useI18n } from '@/contexts/I18nContext';


// Get user trainings for a specific month and year
export const useTrainings = (params: GetTrainingsParams) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['trainings', user?.id, params.month, params.year],
    queryFn: async (): Promise<Training[]> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('get_user_trainings_v6', {
          p_user_id: user.id,
          p_month: params.month,
          p_year: params.year
        });

      if (error) throw error;

      return data || [];
    },
    enabled: !!user,
  });
};

// Get monthly totals for a year
export const useMonthlyTotals = (year: number) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['monthly-totals', user?.id, year],
    queryFn: async (): Promise<MonthlyTotal[]> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('get_monthly_totals', {
          p_user_id: user.id,
          p_year: year
        });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });
};

// Get monthly classes count for a year
export const useMonthlyClasses = (year: number) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['monthly-classes', user?.id, year],
    queryFn: async (): Promise<MonthlyTotal[]> => {
      if (!user) throw new Error('User not authenticated');
      const { data, error } = await supabase
        .rpc('get_monthly_totals_detailed', {
          p_user_id: user.id,
          p_year: year
        });
      if (error) throw error;
      // Transform the data to match MonthlyTotal interface
      return (data || []).map(item => ({
        month: item.month,
        completed_count: item.classes_count || 0
      }));
    },
    enabled: !!user,
  });
};

export const useMonthlyTrainings = (year: number) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['monthly-trainings', user?.id, year],
    queryFn: async (): Promise<MonthlyTotal[]> => {
      if (!user) throw new Error('User not authenticated');
      const { data, error } = await supabase
        .rpc('get_monthly_totals_detailed', {
          p_user_id: user.id,
          p_year: year
        });
      if (error) throw error;
      // Transform the data to match MonthlyTotal interface
      return (data || []).map(item => ({
        month: item.month,
        completed_count: item.trainings_count || 0
      }));
    },
    enabled: !!user,
  });
};

// Update training status
export const useUpdateTrainingStatus = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { t } = useI18n();

  return useMutation({
    mutationFn: async ({ training_id, status }: UpdateTrainingStatusParams): Promise<boolean> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('update_training_status', {
          p_training_id: training_id,
          p_status: status,
          p_user_id: user.id
        });

      if (error) throw error;
      return data;
    },
    onSuccess: async (data, variables) => {
      // Invalidate and refetch trainings queries
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
      queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
      
      // Check training goal progress if status was updated to 'Completed'
      if (variables.status === 'Completed' && user?.id) {
        console.log('🔄 useUpdateTrainingStatus: Training marked as Completed, checking goal progress...', { 
          trainingId: variables.training_id, 
          status: variables.status, 
          userId: user.id 
        });
        try {
          await checkTrainingGoalProgress(user.id, t);
          console.log('✅ useUpdateTrainingStatus: Goal progress check completed successfully');
        } catch (error) {
          console.error('❌ Error checking training goal progress:', error);
        }
      }
    },
  });
};

// Update training notes
export const useUpdateTrainingNotes = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ training_id, notes }: UpdateTrainingNotesParams): Promise<boolean> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('trainings')
        .update({ notes })
        .eq('id', training_id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return !!data;
    },
    onSuccess: () => {
      // Invalidate and refetch trainings queries
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
      queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
    },
  });
};

// Create new training
export const useCreateTraining = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (trainingData: Omit<Training, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('trainings')
        .insert({
          ...trainingData,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch trainings queries
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
      queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
    },
  });
};

// Update existing training
export const useUpdateTraining = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ trainingId, trainingData, instructorId }: { 
      trainingId: string; 
      trainingData: Partial<Omit<Training, 'id' | 'user_id' | 'created_at' | 'updated_at'>>;
      instructorId?: string;
    }) => {
      if (!user) throw new Error('User not authenticated');

      // Update training directly via Supabase client
      const { data, error } = await supabase
        .from('trainings')
        .update(trainingData)
        .eq('id', trainingId)
        .eq('user_id', user.id) // Ensure user can only update their own trainings
        .select()
        .single();

      if (error) throw error;

      // Handle instructor update if provided
      if (instructorId !== undefined) {
        try {
          // Remove existing instructor relationship
          const { error: deleteError } = await supabase
            .from('training_instructors')
            .delete()
            .eq('training_id', trainingId);

          if (deleteError) throw deleteError;

          // Add new instructor relationship if provided
          if (instructorId) {
            const { error: insertError } = await supabase
              .from('training_instructors')
              .insert({
                training_id: trainingId,
                instructor_user_id: instructorId
              });

            if (insertError) throw insertError;
          }
        } catch (error) {
          console.error('Error updating training instructor:', error);
          throw error;
        }
      }

      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch trainings queries
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
      queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-totals'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-classes'] });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
      queryClient.invalidateQueries({ queryKey: ['practitioner-highlights'] });
    },
  });
}; 

// Get user trainings with notes and partners for a specific month and year
export const useTrainingsWithNotes = (params: GetTrainingsParams) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['trainings-with-notes', user?.id, params.month, params.year],
    queryFn: async (): Promise<{ trainings: Training[], notesData: Record<string, any> }> => {
      if (!user) throw new Error('User not authenticated');

      // Get trainings (including classes)
      const { data: trainings, error: trainingsError } = await supabase
        .rpc('get_user_trainings_including_classes', {
          p_user_id: user.id,
          p_month: params.month,
          p_year: params.year
        });

      if (trainingsError) throw trainingsError;

      // Get session token for Edge Functions
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) throw new Error('No access token');

      // Get notes and partners for all trainings (not just completed ones)
      const trainingIds = trainings?.map(t => t.id) || [];

      let notesData: Record<string, any> = {};

      if (trainingIds.length > 0) {
        try {
          // Use local Supabase functions URL in development
          const baseUrl = import.meta.env.DEV
            ? 'http://127.0.0.1:54321'
            : import.meta.env.VITE_SUPABASE_URL;

          const response = await fetch(`${baseUrl}/functions/v1/get_batch_training_notes`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ training_ids: trainingIds }),
          });

          if (response.ok) {
            const result = await response.json();
            notesData = result.data || {};
          } else {
            console.warn('Failed to fetch batch training notes:', response.status);
          }
        } catch (error) {
          console.warn('Error fetching batch training notes:', error);
        }
      }

      return {
        trainings: trainings || [],
        notesData
      };
    },
    enabled: !!user,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get user classes with notes and partners for a specific month and year
export const useClassesWithNotes = (params: GetTrainingsParams) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['classes-with-notes', user?.id, params.month, params.year],
    queryFn: async (): Promise<{ classes: Training[], notesData: Record<string, any> }> => {
      if (!user) throw new Error('User not authenticated');

      // Get classes
      const { data: classes, error: classesError } = await supabase
        .rpc('get_user_classes_only', {
          p_user_id: user.id,
          p_month: params.month,
          p_year: params.year
        });

      if (classesError) throw classesError;

      // Get session token for Edge Functions
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) throw new Error('No access token');

      // Get notes and partners for all classes (not just completed ones)
      const classIds = classes?.map(c => c.id) || [];

      let notesData: Record<string, any> = {};

      if (classIds.length > 0) {
        try {
          // Use local Supabase functions URL in development
          const baseUrl = import.meta.env.DEV
            ? 'http://127.0.0.1:54321'
            : import.meta.env.VITE_SUPABASE_URL;

          const response = await fetch(`${baseUrl}/functions/v1/get_batch_training_notes`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ training_ids: classIds }),
          });

          if (response.ok) {
            const result = await response.json();
            notesData = result.data || {};
          } else {
            console.warn('Failed to fetch batch class notes:', response.status);
          }
        } catch (error) {
          console.warn('Error fetching batch class notes:', error);
        }
      }

      return {
        classes: classes || [],
        notesData
      };
    },
    enabled: !!user,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}; 